import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FaUserPlus, FaMoneyBillWave, FaUsers, FaSearch, FaPlus, FaMoneyBillAlt } from 'react-icons/fa';
import axios from 'axios';
import { ENDPOINT } from '../api/auth';
import { useAuth } from '../context/AuthContext';
import './WorkersCredit.css';

const WorkersCredit = () => {
  const { user } = useAuth();
  const [workers, setWorkers] = useState([]);
  const [filteredWorkers, setFilteredWorkers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    workerId: '',
    amount: '',
    paymentAmount: ''
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [paying, setPaying] = useState(false);
  const [activeTab, setActiveTab] = useState('add'); // 'add' or 'pay' or 'overview'

  // Fetch workers with credit
  const fetchWorkers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${ENDPOINT}?action=get_workers_with_credit`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        const workersData = response.data.workers || [];
        setWorkers(workersData);
        setFilteredWorkers(workersData);
      } else {
        throw new Error(response.data.message || 'Failed to fetch workers');
      }
    } catch (error) {
      console.error('Error fetching workers:', error);
      toast.error(error.response?.data?.message || 'Failed to load workers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkers();
  }, []);

  // Filter workers based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredWorkers(workers);
    } else {
      const filtered = workers.filter(worker =>
        worker.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredWorkers(filtered);
    }
  }, [searchTerm, workers]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddCredit = async (e) => {
    e.preventDefault();
    
    if (!formData.workerId) {
      toast.error('Please select a worker');
      return;
    }

    const creditAmount = parseFloat(formData.amount);
    if (isNaN(creditAmount) || creditAmount <= 0) {
      toast.error('Please enter a valid positive amount');
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${ENDPOINT}?action=add_worker_credit`,
        {
          worker_id: formData.workerId,
          amount: creditAmount
        },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        toast.success('Credit added successfully');
        setFormData(prev => ({ ...prev, amount: '' }));
        fetchWorkers();
      } else {
        throw new Error(response.data.message || 'Failed to add credit');
      }
    } catch (error) {
      console.error('Error adding credit:', error);
      toast.error(error.response?.data?.message || 'Failed to add credit');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePayCredit = async (e) => {
    e.preventDefault();
    
    if (!formData.workerId) {
      toast.error('Please select a worker');
      return;
    }

    const payAmount = parseFloat(formData.paymentAmount);
    if (isNaN(payAmount) || payAmount <= 0) {
      toast.error('Please enter a valid positive amount');
      return;
    }

    const worker = workers.find(w => w.id === formData.workerId);
    if (worker && payAmount > worker.credit) {
      toast.error('Payment amount cannot be greater than the outstanding credit');
      return;
    }

    try {
      setPaying(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${ENDPOINT}?action=pay_worker_credit`,
        {
          worker_id: formData.workerId,
          amount: payAmount
        },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        toast.success('Credit paid successfully');
        setFormData(prev => ({ ...prev, paymentAmount: '' }));
        fetchWorkers();
      } else {
        throw new Error(response.data.message || 'Failed to process payment');
      }
    } catch (error) {
      console.error('Error paying credit:', error);
      toast.error(error.response?.data?.message || 'Failed to process payment');
    } finally {
      setPaying(false);
    }
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",") + ' ETB';
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading workers data...</p>
      </div>
    );
  }

  return (
    <div className="workers-credit">
      <div className="page-header">
        <h1><FaMoneyBillAlt className="header-icon" /> Workers Credit Management</h1>
        <p>Manage worker credits and payments</p>
      </div>

      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'add' ? 'active' : ''}`}
          onClick={() => setActiveTab('add')}
        >
          <FaPlus /> Add Credit
        </button>
        <button 
          className={`tab ${activeTab === 'pay' ? 'active' : ''}`}
          onClick={() => setActiveTab('pay')}
        >
          <FaMoneyBillWave /> Pay Credit
        </button>
        <button 
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <FaUsers /> Overview
        </button>
      </div>

      <div className="tab-content">
        {/* Add Credit Tab */}
        {activeTab === 'add' && (
          <div className="card">
            <h2><FaUserPlus /> Add Worker Credit</h2>
            <form onSubmit={handleAddCredit} className="credit-form">
              <div className="form-group">
                <label htmlFor="worker">Select Worker</label>
                <select
                  id="worker"
                  name="workerId"
                  value={formData.workerId}
                  onChange={handleInputChange}
                  className="form-control"
                  required
                >
                  <option value="">Select a worker...</option>
                  {workers.map(worker => (
                    <option key={worker.id} value={worker.id}>
                      {worker.name} (Credit: {formatCurrency(worker.credit)})
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="amount">Amount to Add (ETB)</label>
                <div className="input-with-icon">
                  <input
                    type="number"
                    id="amount"
                    name="amount"
                    value={formData.amount}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="0.00"
                    step="0.01"
                    min="0.01"
                    required
                  />
                  <span className="input-prefix">ETB</span>
                </div>
              </div>
              
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={submitting}
              >
                {submitting ? 'Adding...' : 'Add Credit'}
              </button>
            </form>
          </div>
        )}

        {/* Pay Credit Tab */}
        {activeTab === 'pay' && (
          <div className="card">
            <h2><FaMoneyBillWave /> Pay Worker Credit</h2>
            <form onSubmit={handlePayCredit} className="credit-form">
              <div className="form-group">
                <label htmlFor="payWorker">Select Worker</label>
                <select
                  id="payWorker"
                  name="workerId"
                  value={formData.workerId}
                  onChange={handleInputChange}
                  className="form-control"
                  required
                >
                  <option value="">Select a worker to pay...</option>
                  {workers
                    .filter(worker => worker.credit > 0)
                    .map(worker => (
                      <option key={worker.id} value={worker.id}>
                        {worker.name} (Credit: {formatCurrency(worker.credit)})
                      </option>
                    ))}
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="paymentAmount">Amount to Pay (ETB)</label>
                <div className="input-with-icon">
                  <input
                    type="number"
                    id="paymentAmount"
                    name="paymentAmount"
                    value={formData.paymentAmount}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="0.00"
                    step="0.01"
                    min="0.01"
                    required
                  />
                  <span className="input-prefix">ETB</span>
                </div>
              </div>
              
              <button 
                type="submit" 
                className="btn btn-danger"
                disabled={paying}
              >
                {paying ? 'Processing...' : 'Pay Credit'}
              </button>
            </form>
          </div>
        )}

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="card">
            <div className="card-header">
              <h2><FaUsers /> Workers Credit Overview</h2>
              <div className="search-box">
                <FaSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Search workers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>
            </div>
            
            <div className="workers-list">
              {filteredWorkers.length > 0 ? (
                <table className="workers-table">
                  <thead>
                    <tr>
                      <th>Worker Name</th>
                      <th className="text-right">Credit Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredWorkers.map(worker => (
                      <tr key={worker.id} className={worker.credit > 0 ? 'has-credit' : ''}>
                        <td>{worker.name}</td>
                        <td className={`text-right ${worker.credit > 0 ? 'text-danger' : 'text-success'}`}>
                          {formatCurrency(worker.credit)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="no-results">
                  <p>No workers found matching your search.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkersCredit;
